import { useEffect, useState } from 'react';

import { ProfessionalAvatar } from '@/components/ProfessionalAvatar';
import { formatPracticeWelcome } from '@/util/practice';
import { LandingPageFooter } from '@/pages/onboarding/LandingPageFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { useResetStaleMmgBorneSession } from '@/hooks/useResetStaleMmgBorneSession';
import { useRegistrationStatus } from '@/hooks/useRegistrationStatus';
import { Practice } from '@aldebaran/prisma';

interface LandingMmgProps {
  practice: Practice | undefined;
  consultationDate: Date | undefined;
  /** The effective professional's welcome message, already filtered by their
   * showWelcomeMessage toggle (getVisibleWelcomeMessage). */
  welcomeMessage?: string | null;
  /** Opened from the kiosk continuation SMS: the patient has checked in already
   * (ALDO-2759). */
  isKioskContinuation?: boolean;
  onRegister: () => void;
}

// Shown on both the QR page and the borne while the on-call doctor has stopped
// new check-ins (ALDO-2800). The body is clinical guidance signed off by the
// MMG's lead doctor: change it with them.
const REGISTRATION_CLOSED_TITLE =
  "Le médecin de garde n'est plus en mesure de voir d'autres patients ce jour";
const REGISTRATION_CLOSED_BODY =
  "Merci d'appeler le 15 pour bénéficier d'un conseil médical ou d'une orientation vers les urgences";

// The kiosk continuation's subtitle, in place of the welcome message the
// patient already read on the kiosk (ALDO-2759)
const KIOSK_CONTINUATION_MESSAGE =
  'Votre arrivée est bien enregistrée. Ces quelques questions aideront le médecin de garde à préparer votre prise en charge';

const MALE_AVATAR_PLACEHOLDER = {
  firstname: '',
  lastname: '',
  title: '',
  avatarId: null,
  gender: 'MALE' as const,
};

/**
 * MMG (Maisons Médicales de Garde) check-in landing — the dedicated welcome page
 * for both `mmg` (patient phone / QR) and `mmg_borne` (waiting-room kiosk). The
 * patient is checking themselves in for on-call care, so there is no doctor to
 * pick: we greet them with the practice name, show the on-call team's welcome
 * message (omitted when empty), and a single "S'enregistrer" CTA (ALDO-2540).
 */
export const LandingMmg = ({
  practice,
  consultationDate,
  welcomeMessage,
  isKioskContinuation = false,
  onRegister,
}: LandingMmgProps) => {
  useResetStaleMmgBorneSession(); // wipes a previous patient's leftover session

  // The on-call doctor can stop new check-ins from the pro app (ALDO-2800).
  // Unknown (loading or failed) counts as open.
  const { data: registrationStatus, refetch: refetchRegistrationStatus } =
    useRegistrationStatus(practice?.id);
  const isClosed = registrationStatus?.acceptingPatients === false;
  const message = isKioskContinuation
    ? KIOSK_CONTINUATION_MESSAGE
    : welcomeMessage;

  // Re-check on tap: a landing loaded before the closure must not let one more
  // patient through. Closed → the re-render swaps in the closed screen.
  const handleRegister = async () => {
    const { data } = await refetchRegistrationStatus();
    if (data?.acceptingPatients === false) return;
    onRegister();
  };

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const avatarSize =
    window.innerWidth >= 640
      ? windowHeight <= (consultationDate ? 580 : 530)
        ? 100
        : 140
      : 120;

  return (
    <PatientPageLayout
      title={
        <div className="flex flex-col items-center justify-center text-center">
          <div className="sm:text-medium sm:px-4 text-3.5xl font-semibold w-full text-secondary">
            {isClosed
              ? REGISTRATION_CLOSED_TITLE
              : practice?.name
              ? formatPracticeWelcome(practice)
              : 'Bienvenue au cabinet'}
          </div>
        </div>
      }
      titleClassName="text-center"
      contentClassName="flex flex-col items-center justify-center text-center text-base"
      footerClassName="!border-0"
      footer={
        isClosed ? null : (
          <div className="w-full flex flex-col items-center justify-center gap-4">
            <LandingPageFooter
              consultationDate={consultationDate}
              buttonLabel={
                isKioskContinuation
                  ? 'Préparer ma consultation'
                  : "S'enregistrer"
              }
              onClick={handleRegister}
            />
          </div>
        )
      }
    >
      <div className="w-full flex flex-col text-center mt-2 sm:mt-0">
        <div className="relative w-full flex items-start justify-center">
          <div className="flex flex-nowrap justify-center gap-x-6 pt-2 mx-6 px-2">
            <div className="flex flex-col justify-center items-center gap-3 w-40 mb-4">
              <ProfessionalAvatar
                professional={{
                  ...MALE_AVATAR_PLACEHOLDER,
                  avatarId: practice?.avatarId ?? null,
                }}
                size={avatarSize}
              />
            </div>
          </div>
        </div>
      </div>
      {isClosed ? (
        // Unlike the welcome message, never hidden on short screens: it is the
        // only thing telling a turned-away patient where to go instead.
        <div className="flex flex-col justify-center items-center gap-y-2 w-full">
          <p className="mt-2 n2xs:w-82.5 max-w-full rounded-3xl bg-white/65 px-6 py-4">
            {REGISTRATION_CLOSED_BODY}
          </p>
        </div>
      ) : (
        message && (
          <div className="flex flex-col justify-center items-center gap-y-2 w-full">
            <p
              className={`mt-2 n2xs:w-82.5 max-w-full rounded-3xl bg-white/65 px-6 py-4 ${
                consultationDate ? 'nsm:mmh44:hidden' : 'nsm:mmh39:hidden'
              }`}
            >
              {message}
            </p>
          </div>
        )
      )}
    </PatientPageLayout>
  );
};
